import axios from "axios";

const defaultGetProgress = (progress: number) => {
    console.log(`Upload progress: ${progress}%`);
};

const HOST = process.env.REACT_APP_HOST;
const API = process.env.REACT_APP_API;

export const handleImageUpload = async (
    file: File,
    getProgress: (progress: number) => void = defaultGetProgress,
    folderName: string = "kyc"
) => {
    if (!file) return;

    try {
        const response = await axios.post(
            `${HOST}${API}apis/v.2.0/common/cc/get-pre-signed-url/`,
            [{
                "folder": folderName,
                "filename": file.name
            }],
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem('x-access-token')
                }
            }
        );

        const preSignedUrl = response.data[0].pre_signed_url;

        await uploadFileInChunksToAzureBlob(file, preSignedUrl, getProgress);
        return response.data;
    } catch (error) {
        if (error instanceof Error) {
            console.error(`Error while uploading an image: ${error.message}`);
        } else {
            console.error("An unknown error occurred while uploading the image.");
        }
    }
};

export const handleDeleteImage = async (imgUrl: string) => {
    try {
        const response = await axios.post(
            `${HOST}${API}apis/v.2.0/common/cc/delete-blob/`,
            { url: imgUrl },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': localStorage.getItem('x-access-token')
                }
            }
        );

        if (response.data.message) {
            console.error('File removed successfully');
        }

        return response.data;
    } catch (error) {
        console.error('Error while removing the file');
    }
};

/**
 * Uploads a file to Azure Blob Storage in chunks (blocks) using a SAS URL.
 * @param file The file to upload
 * @param sasUrl The pre-signed (SAS) URL for the blob (should include the blob name)
 * @param getProgress Optional callback for upload progress (0-100)
 * @param chunkSize Size of each chunk in bytes (default: 4MB)
 */
export const uploadFileInChunksToAzureBlob = async (
    file: File,
    sasUrl: string,
    getProgress: (progress: number) => void = defaultGetProgress,
    chunkSize: number = 512 * 1024
) => {
    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedBytes = 0;
    const blockIds: string[] = [];

    for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        // Block ID must be base64-encoded and unique per block
        const blockId = btoa(`block-${i.toString().padStart(6, '0')}`);
        blockIds.push(blockId);

        // Upload this chunk as a block
        await axios.put(
            `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`,
            chunk,
            {
                headers: {
                    'x-ms-blob-type': 'BlockBlob',
                    'Content-Type': file.type || 'application/octet-stream',
                },
            }
        );
        uploadedBytes += chunk.size;
        getProgress(Math.round((uploadedBytes / file.size) * 100));
    }

    // Commit the block list
    const blockListXml =
        '<?xml version="1.0" encoding="utf-8"?>' +
        '<BlockList>' +
        blockIds.map(id => `<Latest>${id}</Latest>`).join('') +
        '</BlockList>';

    await axios.put(
        `${sasUrl}&comp=blocklist`,
        blockListXml,
        {
            headers: {
                'x-ms-blob-content-type': file.type || 'application/octet-stream',
                'Content-Type': 'application/xml',
            },
        }
    );
    getProgress(100);
};